<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import IconButton from '@spectee/components/Atoms/IconButton/index.vue';
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import Button from '@spectee/components/Atoms/Button/index.vue';
import ScrollBox from '@spectee/components/Atoms/ScrollBox/index.vue';
import messages from '../../../../messages.json';

defineExpose({
  name: 'SideContents',
});

export interface DialogButtonItem {
  tag: 'a' | 'button';
  label: string;
  color?: string;
  dynamics?: string;
  iconName?: string;
  href?: string;
  isDisabled?: boolean;
  click?: () => void;
}

export interface SideContentsProps {
  isOpen?: boolean;
  heading: string | undefined;
  buttonList?: Array<DialogButtonItem>;
  isOverlayClose?: boolean;
  always?: boolean;
}

const props = withDefaults(defineProps<SideContentsProps>(), {
  isOpen: false,
  isInnerVisible: false,
  isOverlayClose: true,
});

const emit = defineEmits(['update:open', 'update:close']);

const isOpen = ref(props.isOpen);
const isInnerVisible = ref(isOpen.value ? true : false);

const close = () => {
  isInnerVisible.value = false;
};

const onOverlayClick = () => {
  if (props.isOverlayClose) {
    close();
  }
};

watch(
  () => props.isOpen,
  async newVal => {
    if (newVal) {
      isOpen.value = newVal;
      await nextTick();
      isInnerVisible.value = true;
      document.body.style.overflow = 'hidden';
    } else {
      isInnerVisible.value = false;
      document.body.style.overflow = '';
    }
  }
);

const afterLeave = () => {
  isOpen.value = false;
  emit('update:close');
};

const afterEnter = () => {
  emit('update:open');
};

const style = ref({});
onMounted(async () => {
  const headerElement = document.querySelector('.Header');

  await nextTick();

  if (headerElement) {
    const headerHeight = parseFloat(
      window.getComputedStyle(headerElement).height
    );
    style.value = {
      top: `${headerHeight}px`,
      height: `calc(100% - ${headerHeight}px)`,
    };
  }
});
</script>

<template>
  <section
    v-if="isOpen"
    class="SideContents"
    :class="{ 'SideContents--overlayClose': isOverlayClose }"
    :style="style"
    @click.stop="onOverlayClick"
    ref="rootRef"
  >
    <transition
      name="slide"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
    >
      <Card v-if="isInnerVisible" class="SideContents__card" @click.stop>
        <div class="SideContents__header">
          <IconButton
            :text="messages.common.close"
            iconName="Close"
            size="large"
            color="black"
            :ariaLabel="messages.common.close"
            direction="down"
            @click="close()"
          />
        </div>
        <div class="SideContents__contents">
          <ScrollBox :always="always">
            <div class="SideContents__contentsInner">
              <Heading
                class="SideContents__heading"
                :heading="heading"
                :level="1"
                size="secondary"
              />
              <slot />
            </div>
          </ScrollBox>
        </div>
        <ul
          class="SideContents__buttonList"
          v-if="buttonList && buttonList.length"
        >
          <li
            v-for="(button, index) in buttonList"
            class="SideContents__buttonItem"
            :key="index"
          >
            <Button
              class="SideContents__button"
              :tag="button.tag"
              size="medium"
              :dynamics="button.dynamics"
              :label="button.label"
              :isDisabled="button.isDisabled"
              @click="button.click ? button.click() : close()"
            />
          </li>
        </ul>
      </Card>
    </transition>
  </section>
</template>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;

.SideContents {
  position: fixed;
  top: 0;
  left: 0;
  z-index: $service-z-index-f + 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: $color-background-shadow;

  &--overlayClose {
    cursor: pointer;
  }

  & &__card {
    display: flex;
    flex-direction: column;
    width: 50%;
    height: 100%;
    padding: $service-unit * 5 0;
    cursor: auto;
    box-shadow: $service-shadow-mf;
  }

  &__header {
    padding: 0 $service-unit * 4;
  }

  &__contents {
    height: 100%;
    margin-top: $service-unit * 2;
    overflow: hidden;
  }

  &__contentsInner {
    padding: 0 $service-unit * 4;
  }

  // & &__heading {
  //   margin-bottom: $service-unit * 4;
  // }

  &__buttonList {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: $service-unit * 5;
  }

  &__buttonItem + &__buttonItem {
    margin-left: $service-unit * 4;
  }

  & &__button {
    min-width: $service-unit * 26;
  }

  @media (max-width: 1024px) {
    & &__card {
      width: 70%;
    }
  }

  @media (max-width: 767px) {
    & &__heading {
      font-size: $service-font-size-ff;
    }
    &__contentsInner {
      min-width: $service-unit * 133;
    }

    & &__card {
      width: 90%;
    }
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.4s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translate3d(100%, 0, 0);
}

.slide-enter-to,
.slide-leave-from {
  transform: translate3d(0, 0, 0);
}
</style>
